import { useEffect, useState } from "react";
import api, { formatApiError } from "../lib/api";
import {
  Dialog, DialogContent, DialogHeader, DialogTitle, DialogFooter,
} from "./ui/dialog";
import { Building2 } from "lucide-react";
import { toast } from "sonner";
import USDateInput from "./USDateInput";

/**
 * "Convert tenant to lease" — for tenants who were created via the standalone
 * Create Tenant flow (or otherwise have no lease yet). Skips the tenant-creation
 * step and only creates the lease, pre-bound to the supplied tenant.
 *
 * Props:
 *   - tenant       { id, full_name, email } | null  (controls open/close)
 *   - onOpenChange(bool)
 *   - onCreated()  callback fired after successful lease creation
 */
export default function ConvertTenantToLeaseDialog({
  tenant, onOpenChange, onCreated,
}) {
  const open = !!tenant;
  const [properties, setProperties] = useState([]);
  const [loadingProps, setLoadingProps] = useState(false);
  const [saving, setSaving] = useState(false);
  const [form, setForm] = useState({
    property_id: "", unit_label: "", monthly_rent: "",
    start_date: "", end_date: "", deposit: "",
  });

  useEffect(() => {
    if (!open) return;
    setForm({
      property_id: "", unit_label: "", monthly_rent: "",
      start_date: "", end_date: "", deposit: "",
    });
    setLoadingProps(true);
    api
      .get("/properties")
      .then((r) => setProperties(r.data || []))
      .catch(() => setProperties([]))
      .finally(() => setLoadingProps(false));
  }, [open]);

  const submit = async (e) => {
    e.preventDefault();
    if (!form.property_id) {
      toast.error("Select the property this tenant occupies");
      return;
    }
    if (!form.monthly_rent || Number(form.monthly_rent) <= 0) {
      toast.error("Monthly rent must be greater than zero");
      return;
    }
    if (!form.start_date || !form.end_date) {
      toast.error("Both lease start and end dates are required");
      return;
    }
    if (form.end_date <= form.start_date) {
      toast.error("Lease end date must be after start date");
      return;
    }
    setSaving(true);
    try {
      await api.post("/leases", {
        property_id: form.property_id,
        tenant_id: tenant.id,
        unit_label: form.unit_label || "Main",
        monthly_rent: Number(form.monthly_rent),
        start_date: form.start_date,
        end_date: form.end_date,
        deposit: Number(form.deposit || 0),
      });
      toast.success(`${tenant.full_name} is now leased to ${form.unit_label || "the property"}`);
      onCreated?.();
      onOpenChange(false);
    } catch (err) {
      toast.error(formatApiError(err.response?.data?.detail) || "Failed to create lease");
    } finally {
      setSaving(false);
    }
  };

  return (
    <Dialog open={open} onOpenChange={onOpenChange}>
      <DialogContent className="sm:max-w-lg" data-testid="convert-tenant-to-lease-dialog">
        <DialogHeader>
          <DialogTitle style={{ fontFamily: "Outfit" }} className="flex items-center gap-2">
            <Building2 className="w-5 h-5 text-indigo-600" /> Convert to lease
          </DialogTitle>
          {tenant && (
            <p className="text-sm text-slate-500 mt-1">
              Place <span className="font-semibold text-slate-700">{tenant.full_name}</span> on a
              property — turns this prospect into an active tenancy.
            </p>
          )}
        </DialogHeader>

        <form onSubmit={submit} className="space-y-3 pt-2">
          <Field label="Property">
            <select
              required
              className="input-clean w-full"
              data-testid="ctl-property-select"
              value={form.property_id}
              onChange={(e) => setForm({ ...form, property_id: e.target.value })}
            >
              <option value="">{loadingProps ? "Loading…" : "Select a property"}</option>
              {properties.map((p) => (
                <option key={p.id} value={p.id}>
                  {p.name} — {p.address || "no address"}
                </option>
              ))}
            </select>
          </Field>

          <div className="grid grid-cols-2 gap-3">
            <Field label="Unit label">
              <input
                className="input-clean w-full"
                data-testid="ctl-unit-input"
                placeholder="Suite 100 / Apt 2B / Main"
                value={form.unit_label}
                onChange={(e) => setForm({ ...form, unit_label: e.target.value })}
              />
            </Field>
            <Field label="Monthly rent (USD)">
              <input
                required
                type="number"
                min="1"
                step="0.01"
                className="input-clean w-full"
                data-testid="ctl-rent-input"
                placeholder="2500"
                value={form.monthly_rent}
                onChange={(e) => setForm({ ...form, monthly_rent: e.target.value })}
              />
            </Field>
          </div>

          <div className="grid grid-cols-3 gap-3">
            <Field label="Lease start">
              <USDateInput
                required
                data-testid="ctl-start-input"
                value={form.start_date}
                onChange={(iso) => setForm({ ...form, start_date: iso })}
              />
            </Field>
            <Field label="Lease end">
              <USDateInput
                required
                data-testid="ctl-end-input"
                value={form.end_date}
                onChange={(iso) => setForm({ ...form, end_date: iso })}
              />
            </Field>
            <Field label="Deposit (USD)">
              <input
                type="number"
                min="0"
                step="0.01"
                className="input-clean w-full"
                data-testid="ctl-deposit-input"
                placeholder="0"
                value={form.deposit}
                onChange={(e) => setForm({ ...form, deposit: e.target.value })}
              />
            </Field>
          </div>

          <DialogFooter>
            <button
              type="button"
              onClick={() => onOpenChange(false)}
              className="btn-outline-dark"
              disabled={saving}
            >
              Cancel
            </button>
            <button
              type="submit"
              className="btn-primary-dark disabled:opacity-60"
              data-testid="ctl-submit-btn"
              disabled={saving}
            >
              {saving ? "Creating lease…" : "Create lease"}
            </button>
          </DialogFooter>
        </form>
      </DialogContent>
    </Dialog>
  );
}

function Field({ label, children }) {
  return (
    <label className="block">
      <div className="overline text-slate-600 mb-1">{label}</div>
      {children}
    </label>
  );
}
